import React, { useMemo } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const QuizQuestion = ({ question, selectedAnswer, onAnswerChange, showAnswer }) => {
  // Create a stable shuffled version of options using useMemo
  const shuffledOptions = useMemo(() => {
    if (!question.options) return [];
    
    // Create array of options with original indices
    const optionsWithIndices = question.options.map((option, index) => ({
      option,
      originalIndex: index
    }));
    
    // Simple shuffle algorithm that's deterministic based on question content
    // This ensures the same question always has the same shuffled order
    const shuffled = [...optionsWithIndices];
    const seed = question.question_text.length + question.question_id;
    
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor((Math.sin(seed * (i + 1)) + 1) * 0.5 * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    return shuffled;
  }, [question.options, question.question_text, question.question_id]);
  const handleAnswerSelect = (option) => {
    if (question.multiple_answers) {
      // Handle multiple answer questions
      const currentAnswers = Array.isArray(selectedAnswer) ? selectedAnswer : [];
      let newAnswers;
      
      if (currentAnswers.includes(option)) {
        // Remove the option if it's already selected
        newAnswers = currentAnswers.filter(answer => answer !== option);
      } else {
        // Add the option if it's not selected
        newAnswers = [...currentAnswers, option];
      }
      
      onAnswerChange(newAnswers);
    } else {
      // Handle single answer questions
      onAnswerChange(option);
    }
  };

  const isOptionSelected = (option) => {
    if (question.multiple_answers) {
      return Array.isArray(selectedAnswer) && selectedAnswer.includes(option);
    } else {
      return selectedAnswer === option;
    }
  };

  const isCorrectAnswer = (option) => {
    if (Array.isArray(question.correct_answer)) {
      return question.correct_answer.includes(option);
    } else {
      return question.correct_answer === option;
    }
  };

  const isWrongAnswer = (option) => {
    return showAnswer && isOptionSelected(option) && !isCorrectAnswer(option);
  };
  
  // Helper function to format answer display
  const formatAnswer = (answer) => {
    if (!answer) return '';
    if (Array.isArray(answer)) {
      return answer.join(', ');
    }
    return answer;
  };

  // Use the code snippet that was filtered by the backend
  const getCodeSnippet = () => {
    // Backend now sends a single code_snippet instead of code_snippets
    return question.code_snippet || null;
  };
  
  // Get the appropriate language for syntax highlighting
  const getCodeLanguage = () => {
    // Backend now includes code_language in the question
    return question.code_language || localStorage.getItem('preferredCodeLanguage') || 'javascript';
  };

  return (
    <div className="quiz-question-modern">


      {/* Question Text */}
      <h4 className="question-text-modern mb-4">{question.question_text}</h4>

      {/* Code Snippet */}
      {getCodeSnippet() && (
        <div className="code-snippet-modern mb-4">
          <SyntaxHighlighter 
            language={getCodeLanguage()}
            style={vscDarkPlus}
            customStyle={{
              borderRadius: '8px',
              padding: '1rem',
              backgroundColor: '#1e1e1e',
              fontSize: '0.9rem'
            }}
            wrapLines={true}
            showLineNumbers={true}
          >
            {getCodeSnippet()}
          </SyntaxHighlighter>
        </div>
      )}

      {/* Answer Options */}
      <div>
        <h6 className="options-title-modern mb-3">
          {question.multiple_answers ? 'Select all that apply:' : 'Select one answer:'}
        </h6>
        <div>
          {shuffledOptions.map((optionData, index) => (
            <button
              key={optionData.originalIndex}
              className={`question-option-modern ${
                isOptionSelected(optionData.option) ? 'selected' : ''
              } ${isCorrectAnswer(optionData.option) && showAnswer ? 'correct-answer' : ''} ${
                isWrongAnswer(optionData.option) ? 'wrong-answer' : ''
              }`}
              data-testid={isCorrectAnswer(optionData.option) ? 'correct-answer-option' : ''}
              onClick={() => handleAnswerSelect(optionData.option)}
              disabled={showAnswer} // Disable options if answer is shown
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{optionData.option}</span>
            </button>
          ))}
        </div>
      </div>

      {showAnswer && (
        <div className="quiz-explanation-section mt-4">
          <h5 className="quiz-explanation-title">Correct Answer & Explanation</h5>
          <div className="correct-answer-display mb-3">
            <span className="answer-label">The correct answer is: </span>
            <span className="answer-value" data-testid="correct-answer-text">{formatAnswer(question.correct_answer)}</span>
          </div>
          {question.explanation && (
            <div className="explanation-container">
              <h6 className="explanation-title">Explanation:</h6>
              <p className="quiz-explanation-text">{question.explanation}</p>
            </div>
          )}
          {question.patterns_tested && question.patterns_tested.length > 0 && (
            <p className="quiz-explanation-topic">Topics Tested: <strong>{Array.isArray(question.patterns_tested) ? question.patterns_tested.join(', ') : question.patterns_tested}</strong></p>
          )}
          {question.topic && !question.patterns_tested && (
            <p className="quiz-explanation-topic">Topic Tested: <strong>{question.topic}</strong></p>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizQuestion;
